import { countries } from "@/shared/utils/countries";

/**
 * Countries a client / organization may be onboarded from.
 *
 * ## Why an allowlist and not a blocklist
 *
 * The selectors used to offer the whole ISO 3166-1 catalog (191 entries in `countries.ts`, 218 in
 * `phone-countries.ts`), which let an operator register a client from a jurisdiction we cannot serve. A
 * blocklist would have to be extended every time a new code appears; an allowlist fails closed, so a country
 * only becomes selectable when somebody adds it here on purpose.
 *
 * ## How the list was chosen
 *
 * Grounded in the flows that actually move money, not in a general notion of "supported":
 *
 * - **Payin + payout rails** (`OPERATIONAL_CORRIDOR_CELLS`, `PAYIN_COUNTRIES`, `RUNTIME_CORRIDOR_ISO3`): ARG,
 *   BRA, COL, MEX — the only corridors the payments runtime executes.
 * - **Runtime wallet currencies** (`CURRENCY_TO_COUNTRY`) and the quoter catalog: adds CHL, PER, PRY, GTM, CRI
 *   plus the USD countries ECU and PAN and the EUR block.
 * - **Public accounts** (`PUBLIC_ACCOUNT_COUNTRY_OPTIONS`): adds URY.
 * - **Product-declared markets** (`register.constants.ts`): ESP, PRT.
 *
 * ## Deliberate exclusions
 *
 * - **United States and its territories** (USA, PRI, VIR, GUM, MNP, ASM, UMI) and **Canada** (CAN): regulations
 *   we do not meet.
 * - **Restricted / sanctioned jurisdictions**: VEN, RUS, BLR, CUB, IRN, PRK, SYR, MMR, AFG, NIC.
 * - **CHN and HKG**: present in the public-accounts catalog, but removed at the request of the Binance Pay
 *   counterparty (`PublicAccountPaymentMethod.BINANCE_PAY` settles USDT payouts, so its country policy
 *   constrains who can be onboarded).
 *
 * Everything else is out by default. This is an onboarding gate only — it never hides an already-stored
 * country, which keeps historical records readable (see `withPreservedCountry`).
 */
export const ONBOARDING_COUNTRY_ISO3 = [
  // Operational corridors — payin and payout
  "ARG",
  "BRA",
  "COL",
  "MEX",
  // Rest of LatAm — wallet currency or quoter corridor
  "CHL",
  "CRI",
  "ECU",
  "GTM",
  "PAN",
  "PER",
  "PRY",
  "URY",
  // Europe — the EUR wallet currency
  "DEU",
  "ESP",
  "FRA",
  "ITA",
  "PRT",
] as const;

export type OnboardingCountryIso3 = (typeof ONBOARDING_COUNTRY_ISO3)[number];

const ISO3_SET: ReadonlySet<string> = new Set(ONBOARDING_COUNTRY_ISO3);

/** True when the ISO 3166-1 alpha-3 code may be selected on onboarding. */
export function isOnboardingCountryIso3(code: string | null | undefined): boolean {
  return !!code && ISO3_SET.has(code.trim().toUpperCase());
}

/**
 * The same allowlist as ISO 3166-1 alpha-2, for the pickers whose value is alpha-2 (`ColocaPhoneField`,
 * `ColocaCountryField`, `AccountCountrySelect`). Derived from the shared catalog rather than typed out again,
 * so the two spellings cannot drift.
 */
export const ONBOARDING_COUNTRY_ISO2: readonly string[] = ONBOARDING_COUNTRY_ISO3.map((iso3) => {
  const match = countries.find((c) => c.code === iso3);
  if (!match?.iso2) throw new Error(`Onboarding country ${iso3} has no iso2 in the shared country catalog`);
  return match.iso2;
});

const ISO2_SET: ReadonlySet<string> = new Set(ONBOARDING_COUNTRY_ISO2);

/** True when the ISO 3166-1 alpha-2 code may be selected on onboarding. */
export function isOnboardingCountryIso2(code: string | null | undefined): boolean {
  return !!code && ISO2_SET.has(code.trim().toUpperCase());
}

/**
 * Narrows a country option list to the allowlist, **keeping `current` even when it is excluded**.
 *
 * Edit forms load a country that was stored before this gate existed (or from a country since removed). If
 * the option vanished, the select would render blank over a value that is still in the record, and saving any
 * other field would silently rewrite the country. Keeping the stored option makes the value visible and
 * one-way: the operator can move off it, never back onto it.
 */
export function withPreservedCountry<T extends { value: string }>(
  options: readonly T[],
  current: string | null | undefined,
  isAllowed: (code: string) => boolean,
): T[] {
  const kept = options.filter((option) => isAllowed(option.value));
  const code = current?.trim().toUpperCase();
  if (!code || kept.some((option) => option.value.toUpperCase() === code)) return kept;

  const stored = options.find((option) => option.value.toUpperCase() === code);
  return stored ? [stored, ...kept] : kept;
}
